import { AxiosResponse } from 'axios';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { BaseService } from '@services/route.service';
import { environment } from 'routes/environment.router';
import { authStorage } from 'functions/auth-session';
import {
    MultichannelConversation,
    MultichannelLead,
    MultichannelMessage,
    MultichannelSendResult,
    MultichannelTemplate,
} from './responses/multichannel.response';

interface StreamListener {
    onOpen: () => void;
    onEvent: (event: EventSourceMessage) => void;
}

export class MultichannelService extends BaseService {
    protected get resourceName(): string {
        return 'crm/multichannel';
    }

    /// Sem canal, vem a caixa inteira; cada item do menu manda o seu.
    public async conversations(
        channel?: string
    ): Promise<AxiosResponse<{ data: MultichannelConversation[] }>> {
        return await this.get('conversations', channel ? { channel } : undefined);
    }

    public async messages(
        conversationId: number
    ): Promise<AxiosResponse<{ data: MultichannelMessage[] }>> {
        return await this.get(`conversations/${conversationId}/messages`);
    }

    /// Quem conta o não lido é o Chatwoot, então ele precisa saber que o atendente leu.
    public async markAsRead(conversationId: number): Promise<AxiosResponse<void>> {
        return await this.post(undefined, `conversations/${conversationId}/read`);
    }

    /// O id de conversa só é único dentro de uma inbox, então a inbox vai junto.
    public async lead(
        conversationId: number,
        inboxId: number
    ): Promise<AxiosResponse<{ data: MultichannelLead }>> {
        return await this.get(`conversations/${conversationId}/lead`, { inboxId });
    }

    /// inReplyTo cita uma mensagem; o Chatwoot resolve o resto por canal.
    public async send(
        conversationId: number,
        content: string,
        inReplyTo?: number | null
    ): Promise<AxiosResponse<{ data: MultichannelSendResult }>> {
        return await this.post(
            { content, inReplyTo: inReplyTo ?? null },
            `conversations/${conversationId}/messages`);
    }

    public async templates(): Promise<AxiosResponse<{ data: MultichannelTemplate[] }>> {
        return await this.get('templates');
    }

    /// Variáveis que o CRM preenche nesta conversa, com o valor; a caixa vai junto (id repete).
    public async templateValues(
        conversationId: number,
        inboxId?: number | null
    ): Promise<AxiosResponse<{ data: Record<string, string> }>> {
        return await this.get(`conversations/${conversationId}/template-values`, { inboxId });
    }

    /// values traz só as variáveis livres, na ordem em que aparecem.
    public async sendTemplate(
        conversationId: number,
        inboxId: number | null | undefined,
        name: string,
        values: string[]
    ): Promise<AxiosResponse<{ data: MultichannelSendResult }>> {
        return await this.post(
            { name, values, inboxId },
            `conversations/${conversationId}/messages/template`);
    }

    /// Remove da conversa no Chatwoot; o contato já recebeu e continua vendo.
    public async deleteMessage(
        conversationId: number,
        messageId: number
    ): Promise<AxiosResponse<void>> {
        return await this.delete(`conversations/${conversationId}/messages/${messageId}`);
    }

    public async sendMedia(
        conversationId: number,
        file: File,
        caption?: string
    ): Promise<AxiosResponse<{ data: MultichannelSendResult }>> {
        const form = new FormData();
        form.append('file', file);
        if (caption) form.append('caption', caption);

        return await this.post(form, `conversations/${conversationId}/messages/media`);
    }

    /// Uma conexão por chamada: a promessa termina quando o stream cai e quem chama reconecta.
    /// Chave e token vão no header, não na URL (acabariam em logs); o token é relido a cada conexão.
    public stream(listener: StreamListener, signal: AbortSignal): Promise<void> {
        const token = authStorage.read()?.token;

        const streamUrl = `${environment.api_url.replace(/\/$/, '')}/${this.buildUrl('stream')}`;

        return fetchEventSource(streamUrl, {
            headers: {
                'X-Api-Key': environment.api_key,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal,
            // Sem isto a biblioteca fecha com a aba escondida e reabre com os headers vencidos.
            openWhenHidden: true,
            onopen: async (response) => {
                if (!response.ok) throw new Error(`SSE ${response.status}`);

                listener.onOpen();
            },
            onmessage: listener.onEvent,
            // Lançar desliga a nova tentativa interna (mesmos headers); quem chama reconecta.
            onerror: (error) => {
                throw error;
            },
        });
    }
}
